import { useReducer, useCallback } from 'react';

import {
  setEditorStateAction,
  toggleIsUpdatingAction,
  updateImageAction,
  updatePostAction,
  updateGifAction,
  postAddMentionAction,
  postAddTagAction,
  clearPostDataAction,
  updateTagsRecommendationsAction,
  updateManualVisibilityUpdateStatusAction,
  updateAddPostVisibilityAction,
  updateSentimentAction,
} from 'actions/addPost';
import Media from 'models/media';
import { post, postDataInitialState } from 'reducers/addPost';

import { SENTIMENTS } from 'constants/ProjectConstants';
import { generateKey } from 'constants/helper';

export default function useCommentReducer(contentState, media, visibility, sentiment = SENTIMENTS.neutral) {
  const [commentState, commentDispatch] = useReducer(
    post,
    postDataInitialState(contentState, media, null, visibility, sentiment)
  );

  const {
    setImage,
    setEditorState,
    toggleIsUpdating,
    updatePost: updateComment,
    addTag,
    addMention,
    setGif,
    clearPost,
    updateVisibility,
    updateSentiment,
  } = usePostState(commentDispatch);

  // commented out because it was causing errors with PostField, plugins were not working
  // useEffect(() => {
  //   updateComment(postDataInitialState(contentState, media, visibility));
  // }, [JSON.stringify(contentState), JSON.stringify(media), visibility]);

  return {
    commentState,
    setUploadedFile: setImage,
    setEditorState,
    toggleIsUpdating,
    updateComment,
    clearComment: clearPost,
    setGif,
    addTag,
    updateVisibility,
    updateSentiment,
    addMention,
  };
}

export function usePostState(dispatch) {
  const setEditorState = useCallback(
    (editorState) => {
      dispatch(setEditorStateAction(editorState));
    },
    [dispatch]
  );

  const toggleIsUpdating = useCallback(() => {
    dispatch(toggleIsUpdatingAction());
  }, [dispatch]);

  // for updating the image that is part of the comment or post state
  const setImage = useCallback(
    (imageMediaObject) => {
      dispatch(updateImageAction(imageMediaObject));
    },
    [dispatch]
  );

  // for updating gif that is part of the comment or post
  const setGif = useCallback(
    (gifUrl) => {
      const gifMediaObject = gifUrl ? { uuid: generateKey(5), type: Media.TYPES.gif, url: gifUrl } : null;
      dispatch(updateGifAction(gifMediaObject));
    },
    [dispatch]
  );

  const updatePost = useCallback(
    (post = {}) => {
      dispatch(updatePostAction(post));
    },
    [dispatch]
  );

  const updateVisibility = useCallback(
    (newVisibility) => {
      dispatch(updateAddPostVisibilityAction(newVisibility));
    },
    [dispatch]
  );

  const updateSentiment = useCallback(
    (newSentiment) => {
      dispatch(updateSentimentAction(newSentiment));
    },
    [dispatch]
  );

  // for adding @employee tag to editorstate
  // in postaddmentionaction component employee is converted to mention data
  const addMention = useCallback(
    (employee = {}) => {
      dispatch(postAddMentionAction(employee));
    },
    [dispatch]
  );

  const addTag = useCallback(
    (tagData = {}) => {
      dispatch(postAddTagAction(tagData));
    },
    [dispatch]
  );

  //  for clearing editorstate, isUpdating, images, gifs of a comment or post
  // this doesn't set to null the uploaded file object which we use for preview
  // while our image is uploaded to the server
  const clearPost = useCallback(() => {
    dispatch(clearPostDataAction());
  }, [dispatch]);

  const clearPostTagsMeta = useCallback(() => {
    clearPost();
    dispatch(updateTagsRecommendationsAction(false));
    dispatch(updateManualVisibilityUpdateStatusAction(false));
  }, [dispatch, clearPost]);

  return {
    setImage,
    setEditorState,
    toggleIsUpdating,
    updatePost,
    addTag,
    addMention,
    setGif,
    clearPost,
    clearPostTagsMeta,
    updateVisibility,
    updateSentiment,
  };
}
